import React, { useEffect, useState } from 'react'
import { buildTeamShareUrl } from 'utils'
import ShareButton from './ShareButton'
import PromotionLinkModal from './PromotionLinkModal'
import { recordCommunityPostView } from 'utils/teachingTeamApi'
import ViewStats from '../shared/ViewStats'
import CommunityCommentList from './CommunityCommentList'
import CommunityCommentInput from './CommunityCommentInput'
import Linkify from '../shared/Linkify'
import { timeAgo } from 'utils/timeAgo'

function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center" onClick={onClose}>
      <div className="absolute top-4 right-4 flex items-center gap-3 z-10">
        <a
          href={src}
          download
          onClick={(e) => e.stopPropagation()}
          className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
          </svg>
        </a>
        <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white text-xl transition-colors">
          &times;
        </button>
      </div>
      <img src={src} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
    </div>
  )
}

export default function CommunityPostDetailModal({
  post,
  teamId,
  currentUser,
  colors,
  isOwnerOrAdmin,
  canInteract = true,
  liked,
  likesCount,
  commentsCount,
  comments,
  categories,
  isAuthor,
  onClose,
  onLike,
  onEdit,
  onDelete,
  onPin,
  onPostUpdated,
  onCommentCreated,
  onCommentDeleted,
  onRequestJoin,
  onCategoryChange,
  translations = {},
  teamSlug,
}) {
  const [lightboxSrc, setLightboxSrc] = React.useState(null)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [showCategories, setShowCategories] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const [promoModalOpen, setPromoModalOpen] = React.useState(false)
  const [viewStats, setViewStats] = useState(null)

  useEffect(() => {
    if (!post?.id || !teamId) return
    recordCommunityPostView(teamId, post.id)
      .then((data) => {
        if (data.student_views_count !== undefined) {
          setViewStats(data)
        }
      })
      .catch((err) => { if (process.env.NODE_ENV !== 'production') console.warn('Post view tracking failed:', err) })
  }, [post?.id, teamId])
  const canEdit = isAuthor
  const canDelete = isAuthor || isOwnerOrAdmin
  const canPin = isOwnerOrAdmin
  const canChangeCategory = isOwnerOrAdmin && categories && categories.length > 0
  const showMenu = canEdit || canDelete || canPin || canChangeCategory

  const handleCopyLink = async () => {
    const postUrl = buildTeamShareUrl(`/${teamSlug}/community/${post.id}`, {}, currentUser?.inviteCode)
    if (isOwnerOrAdmin) {
      setMenuOpen(false)
      setPromoModalOpen(true)
      return
    }
    try {
      await navigator.clipboard.writeText(postUrl)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = postUrl
      textarea.style.cssText = 'position:fixed;opacity:0;'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    setMenuOpen(false)
  }

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
        {/* Content container — mobile: full screen, desktop: modal card */}
        <div
          className="relative bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-2xl lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-full lg:h-auto max-h-full lg:max-h-[90vh] overflow-y-auto">
          {/* Header — mobile: back arrow, desktop: blue bar */}
          <div className="sticky top-0 z-10">
            {/* Mobile header */}
            <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3">
              <button onClick={onClose} className="flex items-center gap-1 text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-semibold text-gray-900 truncate">
                {post.title || (translations.post_detail || 'Post')}
              </span>
            </div>
            {/* Desktop header — unified blue bar */}
            <div className="hidden lg:flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#E6F0FF' }}>
              <span className="font-semibold truncate max-w-md" style={{ color: '#3366FF' }}>
                {post.title || (translations.post_detail || 'Post')}
              </span>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Post content */}
          <div className="p-4 lg:p-6">
            {/* Author + menu */}
            <div className="flex items-start gap-3 mb-4">
              {post.author?.avatar_url ? (
                <img src={post.author.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold text-sm">
                  {post.author?.name?.charAt(0) || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{post.author?.name}</span>
                  {post.category && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: `${colors?.primary || '#3366FF'}15`, color: colors?.primary || '#3366FF' }}
                    >
                      {post.category.name}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400">{timeAgo(post.created_at)}</span>
              </div>
              {/* Three-dot menu */}
              {showMenu && (
                <div className="relative">
                  <button
                    onClick={() => setMenuOpen((v) => !v)}
                    className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                    </svg>
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1">
                      {canEdit && (
                        <button onClick={() => { setMenuOpen(false); onEdit && onEdit() }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          {translations.edit || 'Edit'}
                        </button>
                      )}
                      {canChangeCategory && (
                        <div>
                          <button
                            onClick={() => setShowCategories((v) => !v)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span>{translations.change_category || 'Change Category'}</span>
                            <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showCategories ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {showCategories && (
                            <div className="border-t border-gray-100 py-1 bg-gray-50">
                              <button
                                onClick={() => { setMenuOpen(false); setShowCategories(false); onCategoryChange && onCategoryChange(null) }}
                                className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${!post.category?.id ? 'text-blue-600 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                              >
                                {translations.no_category || 'No category'}
                              </button>
                              {categories.map((cat) => (
                                <button
                                  key={cat.id}
                                  onClick={() => { setMenuOpen(false); setShowCategories(false); onCategoryChange && onCategoryChange(cat.id) }}
                                  className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${cat.id === post.category?.id ? 'text-blue-600 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                  {cat.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {canPin && (
                        <button onClick={() => { setMenuOpen(false); onPin && onPin() }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          {post.pinned ? (translations.unpin || 'Unpin') : (translations.pin || 'Pin')}
                        </button>
                      )}
                      {teamSlug && (
                        <button onClick={handleCopyLink} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          {copied ? (translations.link_copied || 'Copied!') : (translations.copy_link || 'Copy Link')}
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => { setMenuOpen(false); onDelete && onDelete() }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                          {translations.delete || 'Delete'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Title */}
            {post.title && (
              <h2 className="text-lg font-bold text-gray-900 mb-3">{post.title}</h2>
            )}

            {/* Body — full content */}
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
              <Linkify text={post.body} />
            </div>

            {/* Images — full display */}
            {post.images && post.images.length > 0 && (
              <div className={`mt-4 ${post.images.length === 1 ? '' : 'grid grid-cols-2 gap-2'}`}>
                {post.images.map((img) => (
                  <img
                    key={img.id}
                    src={img.url}
                    alt=""
                    className="w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity object-cover"
                    style={{ maxHeight: post.images.length === 1 ? '400px' : '240px' }}
                    onClick={() => setLightboxSrc(img.url)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Actions bar */}
          <div className="px-4 lg:px-6 py-3 border-t border-gray-100 flex items-center gap-4">
            <button
              onClick={onLike}
              className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
            >
              <svg className="w-4 h-4" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span>{likesCount > 0 ? likesCount : ''}</span>
            </button>
            <span className="flex items-center gap-1.5 text-sm text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>{commentsCount > 0 ? commentsCount : ''}</span>
            </span>

            {/* Share buttons */}
            <div className="ml-auto">
              <ShareButton
                url={buildTeamShareUrl(`/${teamSlug}/community/${post.id}`, {}, currentUser?.inviteCode)}
                title={post.title || ''}
                colors={colors}
                translations={translations}
                utmContent={`post-${post.id}`}
                currentUser={currentUser}
                teamId={teamId}
              />
            </div>
          </div>

          {/* View Stats (managers only) */}
          {viewStats && (
            <ViewStats
              studentViews={viewStats.student_views_count}
              visitorViews={viewStats.visitor_views_count}
              translations={translations}
              className="px-4 lg:px-6 py-2"
            />
          )}

          {/* Comments section */}
          <div className="px-4 lg:px-6 pb-4 lg:pb-6 border-t border-gray-100 pt-3">
            <CommunityCommentList
              postId={post.id}
              teamId={teamId}
              recentComments={comments}
              totalCount={commentsCount}
              currentUser={currentUser}
              isOwnerOrAdmin={isOwnerOrAdmin}
              onCommentDeleted={onCommentDeleted}
              translations={translations}
            />
            <div className="mt-3">
              <CommunityCommentInput
                postId={post.id}
                teamId={teamId}
                currentUser={currentUser}
                canInteract={canInteract}
                onRequestJoin={onRequestJoin}
                onCommentCreated={onCommentCreated}
                translations={translations}
              />
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Image lightbox */}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

      <PromotionLinkModal
        isOpen={promoModalOpen}
        onClose={() => setPromoModalOpen(false)}
        baseUrl={buildTeamShareUrl(`/${teamSlug}/community/${post.id}`, {}, currentUser?.inviteCode)}
        teamId={teamId}
        inviteCode={currentUser?.inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </>
  )
}
